import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="bg-gray-50 py-12">
      <SEO
        title="Privacy Policy | GoodCart"
        description="Learn about how GoodCart collects, uses, and protects your personal information."
        url="https://goodcart.org/privacy"
        type="website"
      />
      
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-lg shadow-md p-8"
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Privacy Policy</h1>
          
          <div className="prose prose-lg max-w-none text-gray-700">
            <p className="text-lg">Last Updated: March 5, 2025</p>
            
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">1. Introduction</h2>
            <p>
              Welcome to GoodCart ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website goodcart.org (the "Site") or use our services.
            </p>
            <p>
              Please read this Privacy Policy carefully. By accessing or using our Site, you acknowledge that you have read, understood, and agree to be bound by all the terms outlined in this Privacy Policy. If you do not agree with our policies and practices, please do not use our Site.
            </p>
            
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.1 Personal Information</h3>
            <p>
              We may collect personal information that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>Create an account</li>
              <li>Sign up for our newsletter</li>
              <li>Contact us through our contact forms</li>
              <li>Participate in surveys or promotions</li>
              <li>Submit product reviews or feedback</li>
            </ul>
            <p>
              This information may include your name, email address, postal address, phone number, and any other information you choose to provide.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.2 Automatically Collected Information</h3>
            <p>
              When you visit our Site, we may automatically collect certain information about your device and usage patterns. This information may include:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Referring website</li>
              <li>Pages viewed and time spent on those pages</li>
              <li>Links clicked</li>
              <li>Search terms used</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">2.3 Cookies and Similar Technologies</h3>
            <p>
              We use cookies and similar tracking technologies to collect information about your browsing activities. Cookies are small text files that are stored on your device when you visit a website. They help us improve your experience by:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>Remembering your preferences and settings</li>
              <li>Understanding how you use our Site</li>
              <li>Personalizing content and recommendations</li>
              <li>Analyzing the performance of our Site</li>
            </ul>
            <p>
              You can control cookies through your browser settings. However, disabling cookies may limit your ability to use certain features of our Site.
            </p>
            
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">3. How We Use Your Information</h2>
            <p>
              We may use the information we collect for various purposes, including:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>Providing, maintaining, and improving our Site and services</li>
              <li>Processing and fulfilling your requests</li>
              <li>Sending you administrative information, such as updates to our terms, conditions, and policies</li>
              <li>Sending you marketing and promotional communications (with your consent)</li>
              <li>Responding to your comments, questions, and requests</li>
              <li>Monitoring and analyzing trends, usage, and activities in connection with our Site</li>
              <li>Detecting, preventing, and addressing technical issues</li>
              <li>Protecting the security and integrity of our Site</li>
            </ul>
            
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">4. How We Share Your Information</h2>
            <p>
              We may share your information in the following circumstances:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li><strong>Service Providers:</strong> We may share your information with third-party service providers who perform services on our behalf, such as web hosting, data analysis, payment processing, and customer service.</li>
              <li><strong>Business Transfers:</strong> If we are involved in a merger, acquisition, or sale of all or a portion of our assets, your information may be transferred as part of that transaction.</li>
              <li><strong>Legal Requirements:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or government agency).</li>
              <li><strong>Protection of Rights:</strong> We may disclose your information to protect the rights, property, or safety of GoodCart, our users, or others.</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">4.1 Affiliate Program</h3>
            <p>
              GoodCart participates in affiliate advertising programs designed to provide a means for sites to earn advertising fees by linking to retail partners. When you click on our affiliate links and make purchases, we may receive a commission at no additional cost to you.
            </p>
            <p>
              Please note that when you click on these links, you will be directed to a retail partner's website, and your interactions there will be governed by that partner's privacy policy and terms of service.
            </p>
            
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">5. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect the security of your personal information. However, please be aware that no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.
            </p>
            
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">6. Your Rights and Choices</h2>
            <p>
              Depending on your location, you may have certain rights regarding your personal information, including:
            </p>
            <ul className="list-disc pl-6 mb-4">
              <li>Accessing, correcting, or deleting your personal information</li>
              <li>Withdrawing your consent at any time (where we rely on consent as the legal basis for processing)</li>
              <li>Objecting to processing of your personal information</li>
              <li>Requesting restriction of processing of your personal information</li>
              <li>Requesting transfer of your personal information</li>
              <li>Opting out of marketing communications</li>
            </ul>
            <p>
              To exercise these rights, please contact us using the information provided in the "Contact Us" section below.
            </p>
            
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">7. Children's Privacy</h2>
            <p>
              Our Site is not intended for children under the age of 13. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe that your child has provided us with personal information, please contact us, and we will take steps to delete such information.
            </p>
            
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">8. Changes to This Privacy Policy</h2>
            <p>
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.
            </p>
            
            <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">9. Contact Us</h2>
            <p>
              If you have any questions or concerns about this Privacy Policy or our privacy practices, please contact us at:
            </p>
            <p className="mb-4">
              Email: privacy@goodcart.org<br />
              Address: 123 Green Street, San Francisco, CA 94111
            </p>
            <p>
              We will respond to your inquiry as soon as possible.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;